import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import Icon from "./Icon";
import { useMeta } from "../hooks";
import { Thread, NewChatModal } from "../pages/Mensajeria";
import { fmtStamp } from "../utils";

const CONVO_ICON = { task: "check", project: "folder", dm: "user", group: "users" };

// Ventana flotante de mensajería (se abre/cierra desde el icono junto al perfil
// y desde la herramienta flotante). Consolida DMs, grupos y canales de tarea/
// proyecto. Permite crear un mensaje directo a un usuario o sobre un proyecto.
export default function MessagingDock({ open, onClose }) {
  const nav = useNavigate();
  const { projects } = useMeta();
  const [convos, setConvos] = useState([]);
  const [sel, setSel] = useState(null);
  const [newChat, setNewChat] = useState(false);
  const [pickProject, setPickProject] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    api.get("/messaging/conversations").then((r) => setConvos(r.data.conversations || [])).catch(() => setConvos([]));
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const shown = q.trim()
    ? convos.filter((c) => `${c.title} ${c.subtitle}`.toLowerCase().includes(q.toLowerCase()))
    : convos;
  const projList = projects.filter((p) => p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 20);

  return (
    <>
      <div className="fixed bottom-4 right-4 z-[55] flex h-[560px] max-h-[80vh] w-[380px] max-w-[92vw] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        {/* Cabecera */}
        <div className="flex items-center gap-2 border-b border-slate-200 bg-brand-600 px-3 py-2.5 text-white">
          {sel && <button className="rounded p-1 hover:bg-white/20" onClick={() => setSel(null)}><Icon name="arrow-up" size={16} className="-rotate-90" /></button>}
          <Icon name="message" size={18} />
          <span className="flex-1 text-sm font-bold">{sel ? sel.title : "Mensajería"}</span>
          {!sel && <button className="rounded p-1 hover:bg-white/20" title="Abrir en grande" onClick={() => { onClose(); nav("/mensajeria"); }}><Icon name="grid" size={15} /></button>}
          <button className="rounded p-1 hover:bg-white/20" onClick={onClose} title="Cerrar"><Icon name="x" size={16} /></button>
        </div>

        {sel ? (
          <div className="min-h-0 flex-1"><Thread convo={sel} onSent={load} /></div>
        ) : (
          <>
            <div className="flex items-center gap-2 border-b border-slate-100 p-2">
              <div className="relative flex-1">
                <Icon name="search" size={14} className="absolute left-2 top-2 text-slate-400" />
                <input className="input w-full pl-7 text-sm" placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              <button className="btn-outline-primary text-xs" onClick={() => setNewChat(true)} title="Mensaje directo o grupo"><Icon name="plus" size={14} /></button>
              <button className="btn-ghost px-2 text-xs" onClick={() => setPickProject((v) => !v)} title="Chat sobre un proyecto"><Icon name="folder" size={15} /></button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {pickProject && (
                <div className="border-b border-slate-100 bg-slate-50 p-1">
                  <div className="px-2 py-1 text-[11px] font-bold uppercase text-slate-400">Abrir chat de proyecto</div>
                  {projList.map((p) => (
                    <button key={p.id} onClick={() => { setSel({ type: "project", id: p.id, title: p.name, subtitle: "Proyecto" }); setPickProject(false); }}
                      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-white">
                      <Icon name="folder" size={15} className="text-brand-500" /><span className="truncate uppercase">{p.name}</span>
                    </button>
                  ))}
                  {projList.length === 0 && <p className="px-2 py-2 text-xs text-slate-400">Sin proyectos.</p>}
                </div>
              )}
              {shown.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-slate-400">Sin conversaciones aún. Pulsa + para escribirle a alguien.</p>
              ) : shown.map((c) => {
                const unread = c.unread > 0;
                return (
                <button key={`${c.type}-${c.id}`} onClick={() => {
                  setSel(c);
                  if (c.unread > 0) {
                    setConvos((arr) => arr.map((x) => x === c ? { ...x, unread: 0 } : x));
                  }
                }}
                  className={`flex w-full items-start gap-2.5 border-b border-slate-50 px-3 py-2.5 text-left hover:bg-slate-50 ${unread ? "bg-brand-50/30" : ""}`}>
                  <div className="relative mt-0.5 shrink-0">
                    <Icon name={CONVO_ICON[c.type] || "message"} size={17} className={unread ? "text-brand-600" : "text-brand-500"} />
                    {unread && <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`truncate text-sm ${unread ? "font-bold text-slate-900" : "font-semibold text-slate-800"}`}>{c.title}</span>
                      {c.last_at && <span className={`shrink-0 text-[11px] ${unread ? "font-semibold text-brand-600" : "text-slate-400"}`}>{fmtStamp(c.last_at)}</span>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`min-w-0 flex-1 truncate text-[11px] ${unread ? "font-medium text-slate-700" : "text-slate-500"}`}>{c.last_body || c.subtitle}</span>
                      {unread && <span className="shrink-0 rounded-tag bg-rose-500 px-1.5 text-[11px] font-bold leading-4 text-white">{c.unread > 9 ? "9+" : c.unread}</span>}
                    </div>
                  </div>
                </button>
              ); })}
            </div>
          </>
        )}
      </div>

      {newChat && <NewChatModal onClose={() => setNewChat(false)} onOpened={(cid) => { load(); setSel({ type: "dm", id: cid, conversation_id: cid, title: "Conversación", subtitle: "Mensaje directo" }); }} />}
    </>
  );
}
